import { Form, Row } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import React, { useEffect, useState } from 'react';
import { ISubj, ISubjPrilozenesoubory } from 'types/amlz';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import TranslateText from 'components/hoc/TranslateText';

import fileDownload from 'services/fileDownload';
import { prepareFileAmlz } from 'services/prepareFile';
import { EEsmStav, IOsobyData } from 'services/spravaEmitenta/C4osoby';

import { ETypSouboru } from 'enums/amlz/typSouboru';

interface IC4duvodOdstraneni {
    personData: ISubj | undefined;
    setPersonDataOdstraneni: (value: ISubj | undefined) => void;
    setSubmitPersonData: (value: ISubj | null) => void;
    idZadosti: number | string;
    osobyResp: IOsobyData | undefined;
}

const C4duvodOdstraneni: React.FC<IC4duvodOdstraneni> = ({
    personData,
    setSubmitPersonData,
    setPersonDataOdstraneni,
    idZadosti,
    osobyResp
}) => {
    const [form] = Form.useForm();
    const [uploading, setUploading] = useState<boolean>();
    const [fileList, setFileList] = useState<IFileList[] | undefined>(
        !personData?.jestatutar
            ? personData?.prilozenesoubory?.map((e, i) => {
                  return {
                      uid: String(i),
                      name: e.orignazev ? e.orignazev : 'bez nazvu',
                      fileName: e.tempnazev ? e.tempnazev : 'a',
                      type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                      size: 0,
                      status: 'done'
                  };
              })
            : []
    );
    const [prilozeneSoubory, setPrilozeneSoubory] = useState<ISubjPrilozenesoubory[]>(
        !personData?.jestatutar ? personData?.prilozenesoubory || [] : []
    );

    const uploadProps = {
        buttonLoading: uploading,
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading' && setUploading) setUploading(true);
            if (data.file.status !== 'uploading' && setUploading) setUploading(false);
            if (data.fileList.length) {
                if (data.file.response) {
                    setPrilozeneSoubory(
                        data.fileList
                            .filter(f => f.status && f.status !== 'error')
                            .map(e => {
                                return {
                                    orignazev: e.name.normalize(),
                                    tempnazev: e.response ? e.response.tempnazev : e.fileName,
                                    oblast: ETypSouboru.SOUBOR_DOKLAD
                                };
                            })
                    );
                    form.validateFields();
                }

                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
            }
        },
        onDownload: (file: UploadFile) => {
            setUploading(true);
            prepareFileAmlz(idZadosti, file.fileName ? file.fileName : '')
                .then(resp => {
                    fileDownload(resp.redirect, file.name ? file.name : 'file');
                })
                .finally(() => setUploading(false));
        },
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    };

    // TODO
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const subjData = {
            ...personData,
            ...fieldsValue,
            stavesm: EEsmStav.ESMODEB,
            ...(prilozeneSoubory.length && {
                prilozenesoubory: prilozeneSoubory
            })
        };
        delete subjData.soubor;
        setSubmitPersonData(subjData);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentOsobaESMdata =
        osobyResp?.osoby &&
        osobyResp.osoby.filter(e => e.jmeno === personData?.jmeno && e.prijmeni === personData.prijmeni)[0];

    return (
        <Form
            preserve={false}
            scrollToFirstError={true}
            form={form}
            layout="vertical"
            initialValues={{
                remember: true
            }}
            onFinish={onFinish}
            name="C4duvod"
            style={{
                width: '100%'
            }}
        >
            <InputComponent
                label="JMENO"
                name="jmeno"
                initialValue={personData?.jmeno}
                type="text"
                maxLength={100}
                disabled={!!currentOsobaESMdata?.jmeno && !!currentOsobaESMdata?.prijmeni}
            />
            <InputComponent
                label="PRIJMENI"
                name="prijmeni"
                initialValue={personData?.prijmeni}
                type="text"
                maxLength={100}
                disabled={!!currentOsobaESMdata?.jmeno && !!currentOsobaESMdata?.prijmeni}
            />

            <InputTextArea
                rows={4}
                name="duvododebrani"
                initialValue={personData?.duvododebrani ? personData.duvododebrani : ''}
                label="DUVOD_ODSTRANENI_OSOBY"
            />
            <Form.Item
                style={{
                    marginTop: 16,
                    marginBottom: 16
                }}
                name="soubor"
                label={<Paragraph text={'VLOZTE_DOKUMENT'} strong={true} />}
                rules={[
                    {
                        required: true,
                        validator() {
                            try {
                                if (!fileList || fileList.length < 1)
                                    return Promise.reject(
                                        <TranslateText>NEVALIDNI_POVINNA_POLOZKA_UPLOAD</TranslateText>
                                    );
                                return Promise.resolve();
                            } catch {
                                return Promise.reject();
                            }
                        }
                    }
                ]}
            >
                <div
                    style={{
                        marginBottom: 22
                    }}
                >
                    <UploadComponent {...uploadProps} id="C4uploadBtn" />
                </div>
            </Form.Item>
            <Row className="button-row">
                <ButtonComponent
                    buttontext="ZPET"
                    id="C4ZpetBtn"
                    type="default"
                    onClick={() => {
                        setSubmitPersonData(null);
                        setPersonDataOdstraneni(undefined);
                    }}
                />
                <ButtonComponent
                    buttontext="POKRACOVAT"
                    id="C4PokracovatBtn"
                    type="primary"
                    onClick={() => form.submit()}
                    loading={uploading}
                />
            </Row>
        </Form>
    );
};

export default C4duvodOdstraneni;
